import { Save } from '@mui/icons-material';
import {
	Box,
	Button,
	Dialog,
	DialogTitle,
	TextField,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Chip,
	SelectChangeEvent,
	Checkbox,
	FormControlLabel
} from '@mui/material';
import { useEffect, useState } from 'react';

interface Usuario {
	uuid: string;
	nome: string;
}

interface NewTopicProps {
	open: boolean;
	close: () => void;
	save: (title: string, usuarioIds: string[]) => void;
	usuarios: Usuario[]; // Lista de todos os usuários disponíveis
	changeSelecionados: (usuariosSelecionados: Usuario[]) => void;
	changePrivado: (privado: boolean) => void;
	privado: boolean;
}

export default function NewTopic({
	close,
	open,
	save,
	usuarios,
	changeSelecionados,
	changePrivado,
	privado
}: NewTopicProps) {
	const [value, setValue] = useState<string>('');
	const [usuariosSelecionados, setUsuariosSelecionados] = useState<Usuario[]>([]);
	const [todosSelecionados, setTodosSelecionados] = useState<boolean>(false);

	const handleSave = () => {
		const usuarioIds = todosSelecionados
			? usuarios.map(u => u.uuid)
			: usuariosSelecionados.map(u => u.uuid);
		save(value, usuarioIds);
		setValue('');
		setUsuariosSelecionados([]);
		setTodosSelecionados(false);
	};

	const handleClose = () => {
		setValue('');
		setUsuariosSelecionados([]);
		setTodosSelecionados(false);
		close();
	};

	const handleSelectUsuario = (event: SelectChangeEvent<string>) => {
		const usuarioId = event.target.value;

		if (usuarioId === 'TODOS') {
			setTodosSelecionados(true);
			setUsuariosSelecionados([]);
		} else {
			const usuario = usuarios.find(u => u.uuid === usuarioId);

			if (usuario && !usuariosSelecionados.find(u => u.uuid === usuarioId)) {
				setUsuariosSelecionados([...usuariosSelecionados, usuario]);
			}
		}
	};

	const handleRemoveUsuario = (usuarioId: string) => {
		setUsuariosSelecionados(usuariosSelecionados.filter(u => u.uuid !== usuarioId));
	};

	const handleRemoveTodos = () => {
		setTodosSelecionados(false);
	};

	// Filtrar usuários que ainda não foram selecionados
	const usuariosDisponiveis = usuarios.filter(
		u => !usuariosSelecionados.find(selected => selected.uuid === u.uuid)
	);

	const temUsuariosSelecionados = todosSelecionados || usuariosSelecionados.length > 0;

	useEffect(() => {
		changeSelecionados(usuariosSelecionados);
	}, [usuariosSelecionados]);

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>Novo Tópico</DialogTitle>
			<Box sx={{
				padding: 2,
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				justifyContent: 'center',
				minWidth: 400
			}}>
				<TextField
					label='Título do tópico'
					onChange={(e) => setValue(e.target.value)}
					type='text'
					value={value}
					fullWidth
				/>

				<FormControl fullWidth>
					<InputLabel id="usuarios-label">Usuários</InputLabel>
					<Select
						labelId="usuarios-label"
						label="Usuários"
						value=""
						onChange={handleSelectUsuario}
						disabled={todosSelecionados || (usuariosDisponiveis.length === 0 && !todosSelecionados)}
					>
						{!todosSelecionados && (
							<MenuItem value="TODOS">
								<strong>Todos os usuários</strong>
							</MenuItem>
						)}
						{usuariosDisponiveis.map((usuario) => (
							<MenuItem key={usuario.uuid} value={usuario.uuid}>
								{usuario.nome}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Chips dos usuários selecionados */}
				{temUsuariosSelecionados && (
					<Box sx={{
						display: 'flex',
						gap: 1,
						flexWrap: 'wrap',
						marginTop: -1
					}}>
						{todosSelecionados ? (
							<Chip
								label="Todos os usuários"
								onDelete={handleRemoveTodos}
								size="medium"
								sx={{
									backgroundColor: '#1976d2',
									color: '#fff',
									'& .MuiChip-deleteIcon': {
										color: '#fff'
									}
								}}
							/>
						) : (
							usuariosSelecionados.map((usuario) => (
								<Chip
									key={usuario.uuid}
									label={usuario.nome}
									onDelete={() => handleRemoveUsuario(usuario.uuid)}
									size="medium"
									sx={{
										backgroundColor: '#e0e0e0',
										color: '#666'
									}}
								/>
							))
						)}
					</Box>
				)}

				<FormControlLabel control={<Checkbox checked={privado} onChange={(e) => changePrivado(e.target.checked)} />} label="Tópico privado" />

				<Button
					startIcon={<Save />}
					onClick={handleSave}
					variant='contained'
					color='success'
					disabled={!temUsuariosSelecionados}
				>
					Salvar
				</Button>
			</Box>
		</Dialog>
	);
}